import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Chip, CircularProgress, Stack, Typography} from 'views/components';
import {Check} from 'views/icons';
import {CommandResponseView, ConnectionBadge, HoverToolBox, MarkdownViewer, Translate, translate, useCommand} from 'views/module';
import {TASK_TYPES} from './utils/constants';

const TaskView = (props) => {
  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [mouseOver, setMouseOver] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const task = props.task;
  const handleSetStatusToComplete = props.handleSetStatusToComplete;
  const onClickEditTask = props.onClickEditTask;
  const removeTask = props.removeTask;
  const hideEditAction = props.hideEditAction;
  const playbooks = props.playbooks;
  const requester = props.requester;
  const skills = props.skills;
  const progressWrap = props.statusProgressWrap;

  const title = task.title;
  const description = task.description.blob;
  const dueDate = task.dueDate;
  const rawConnections = React.useMemo(() => {
    return Array.isArray(task.connections) ? task.connections : [];
  }, [task.connections]);
  const playbook = playbooks.find((pb) => pb.playbookRn === task.playbookRn);
  const taskType = task.type;
  const mappedTaskTypeConfig = taskType ? TASK_TYPES[taskType] : null;
  const taskTypeConfig = taskType ? (mappedTaskTypeConfig ?? TASK_TYPES.UNKNOWN) : null;
  const taskTypeLabel = mappedTaskTypeConfig?.labelKey ? translate(mappedTaskTypeConfig.labelKey) : taskType;
  const taskComplete = task.status === 'COMPLETE';
  const connections = React.useMemo(() => {
    if (Array.isArray(task.connectionInfo) && task.connectionInfo.length > 0) {
      return task.connectionInfo;
    }

    if (rawConnections.length > 0) {
      return rawConnections.map((connectionRn) => ({
        connectionRn,
      }));
    }

    return [];
  }, [rawConnections, task.connectionInfo]);

  const command = useCommand({
    requester,
    skills,
    statusProgressWrap: progressWrap,
    task,
  });

  React.useEffect(() => {
    setToolbarVisible(mouseOver);
  }, [mouseOver]);

  const handleApproveAndSendSuccess = () => {
    handleSetStatusToComplete(task.taskRn, taskComplete);
  };

  return (
    <Box>
      <Box
        onMouseOver={(e) => setMouseOver(true) & setAnchorEl(e.currentTarget)}
        onMouseLeave={() => setMouseOver(false)}
        sx={{
          backgroundColor: toolbarVisible ? 'background.highlighted' : 'inherit',
          p: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: '2px',
          }}
        >
          <Box
            onClick={() => handleSetStatusToComplete(task.taskRn, taskComplete)}
            sx={{
              'width': '14px',
              'height': '14px',
              'borderRadius': '50%',
              'border': '1.5px solid #D8D3CC',
              'backgroundColor': taskComplete ? '#DA7955' : '#F2F0EC',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'cursor': taskComplete ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: taskComplete ? '#DA7955' : '#F2F0EC',
              },
            }}
          >
            {
              taskComplete &&
              <Check
                sx={{
                  fontSize: '12px',
                  color: 'white',
                }}
              />
            }
          </Box>
        </Box>
        <Stack spacing={1} sx={{opacity: taskComplete ? 0.45 : 1}}>
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            alignItems={{xs: 'flex-start', sm: 'center'}}
            justifyContent='space-between'
            spacing={1}
            sx={{
              pr: {xs: 0, sm: 5},
            }}
          >
            <Stack direction='row' alignItems='center' spacing={2}>
              {
                taskType &&
                <Chip
                  label={taskTypeLabel}
                  variant='outlined'
                  size='small'
                  sx={{
                    fontWeight: 600,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderWidth: 1,
                    ...taskTypeConfig?.chipSx,
                  }}
                />
              }
              <Stack direction='row' alignItems='center' spacing={1}>
                <Typography
                  variant='subtitle1'
                  sx={{
                    fontWeight: 600,
                    textDecoration: taskComplete ? 'line-through' : 'none',
                    fontSize: '13px',
                  }}
                >
                  {title}
                </Typography>
                {
                  command.canRunCommand &&
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {
                        command.isExecutingCommand &&
                          <CircularProgress
                            size={16}
                            sx={{
                              color: '#4B4843',
                            }}
                          />
                      }
                      {
                        !command.isExecutingCommand &&
                          <Button
                            variant='outlined'
                            size='small'
                            onClick={command.runCommand}
                            sx={{
                              minWidth: '30px',
                              px: '12px',
                              py: '8px',
                              borderRadius: '7px',
                              borderColor: '#D8D4CE',
                              background: '#FCFCFC',
                              color: '#4B4843',
                              fontSize: '11px',
                              fontWeight: 500,
                              lineHeight: '100%',
                              textTransform: 'none',
                              alignSelf: 'flex-start',
                            }}
                          >
                            <Translate expression='tasks.Common.Run' />
                          </Button>
                      }
                    </Box>
                }
              </Stack>
            </Stack>
          </Stack>

          <Stack>
            {
              description &&
              <Stack direction='row' spacing={1} alignItems='flex-start'>
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: 400,
                    color: '#6B6760',
                    fontSize: '12px',
                    lineHeight: '18px',
                  }}
                >
                  <Translate expression='caregiver.CaregiverDetail.Tasks.TabLabels.Description' />
                </Typography>
                <Box
                  sx={{
                    'fontSize': '12px',
                    'lineHeight': '18px',
                    '& p': {
                      fontSize: '12px',
                      lineHeight: '18px',
                      m: 0,
                    },
                    '& *': {
                      fontSize: '12px',
                    },
                  }}
                >
                  <MarkdownViewer value={description} />
                </Box>
              </Stack>
            }
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 400,
                  color: '#6B6760',
                  fontSize: '12px',
                }}
              >
                <Translate expression='caregiver.CaregiverDetail.Tasks.TabLabels.DueDate' />
                {' '}
                {
                  new Date(dueDate).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                }
              </Typography>
              {
                connections.length > 0 &&
                  <>
                    <Box
                      sx={{
                        height: 4,
                        width: 4,
                        borderRadius: '50%',
                        backgroundColor: 'text.secondary',
                      }}
                    />
                    <Stack
                      direction='row'
                      spacing={-.5}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      {
                        connections.map((connection, index) => (
                          <ConnectionBadge
                            key={connection.connectionRn}
                            connection={connection}
                            sx={{
                              width: '20px',
                              height: '20px',
                              position: 'relative',
                              zIndex: index + 1,
                              fontSize: '10px',
                              outline: '2px solid',
                              outlineColor: 'background.paper',
                            }}
                          />
                        ))
                      }
                    </Stack>
                  </>
              }
              {
                Boolean(playbook) &&
                  <>
                    <Box
                      sx={{
                        height: 4,
                        width: 4,
                        borderRadius: '50%',
                        backgroundColor: 'text.secondary',
                      }}
                    />
                    <Typography
                      variant='body2'
                      component='button'
                      onClick={() => window.open(`/playbooks?selectedPlaybookRn=${encodeURIComponent(playbook.playbookRn)}`)}
                      sx={{
                        'background': 'none',
                        'border': 'none',
                        'color': '#DA7955',
                        'cursor': 'pointer',
                        'fontSize': '12px',
                        'p': 0,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      /{playbook.title}
                    </Typography>
                  </>
              }
            </Stack>
            {
              command.response &&
                <Box sx={{mt: 1}}>
                  <CommandResponseView
                    communicationTarget={command.communicationTarget}
                    requester={requester}
                    response={command.response}
                    onApproveAndSendSuccess={handleApproveAndSendSuccess}
                    onDiscard={command.discard}
                    isRegenerating={command.isExecutingCommand}
                    onRegenerate={command.regenerate}
                  />
                </Box>
            }
          </Stack>
        </Stack>

        <HoverToolBox
          toolBar={toolbarVisible}
          anchorEl={anchorEl}
          onClickEdit={() => onClickEditTask?.(task)}
          onClickDelete={() => removeTask(task)}
          hideTool={{
            emoji: true,
            copy: true,
            edit: hideEditAction,
            delete: false,
            reply: true,
          }}
        />
      </Box>
    </Box>
  );
};

TaskView.propTypes = {
  handleSetStatusToComplete: PropTypes.func.isRequired,
  task: PropTypes.object.isRequired,
  onClickEditTask: PropTypes.func,
  removeTask: PropTypes.func.isRequired,
  hideEditAction: PropTypes.bool,
  playbooks: PropTypes.array,
  requester: PropTypes.object.isRequired,
  skills: PropTypes.array.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
  requester: state.user,
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskView));
